import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#schedule", label: "Schedule" },
  { href: "#dress-code", label: "Dress Code" },
  { href: "#travel", label: "Travel" },
  { href: "/accommodation", label: "Accommodation" },
  { href: "/rsvp", label: "RSVP" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When NOT on home page, force solid nav background for readability
  const isSolid = scrolled || !onHome;

  const handleNav = (e, href) => {
    if (href.startsWith("#")) {
      if (onHome) {
        // Let default anchor behaviour scroll smoothly via CSS scroll-behavior: smooth
        return;
      }
      // Off-home — navigate to home + hash
      e.preventDefault();
      navigate("/" + href);
      setOpen(false);
    } else {
      // Route link — use SPA navigation
      e.preventDefault();
      navigate(href);
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isSolid
          ? "backdrop-blur-xl bg-[#FAF9F6]/85 border-b border-[#6A1E2D]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate("/"); }}
          data-testid="nav-logo-link"
          className={`font-display text-xl md:text-2xl tracking-wide transition-colors ${
            isSolid ? "text-[#1A1818]" : "text-[#FAF9F6]"
          }`}
        >
          M <span className="text-[#6A1E2D] mx-1">&</span> N
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNav(e, l.href)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={`text-xs tracking-luxe uppercase font-medium transition-colors hover:text-[#6A1E2D] ${
                isSolid ? "text-[#1A1818]" : "text-[#FAF9F6]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 transition-colors ${isSolid ? "text-[#1A1818]" : "text-[#FAF9F6]"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#FAF9F6] border-t border-[#6A1E2D]/10" data-testid="nav-mobile-panel">
          <nav className="flex flex-col px-6 py-6 gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNav(e, l.href)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="text-sm tracking-luxe uppercase text-[#1A1818] hover:text-[#6A1E2D]"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
